import { VisualScreen } from 'app/frontend/types/custom/VisualPlan'
import { decodeJsonBase64, encodeJsonBase64 } from '../planStringTransfer'

export type VisualPlanTransferPayload = {
  screens: VisualScreen[]
}

export type VisualPlanImportSuccess = {
  ok: true
  screens: VisualScreen[]
  objectCount: number
  screenCount: number
}

export type VisualPlanImportFailure = {
  ok: false
  error: 'invalid'
}

export type VisualPlanImportResult =
  | VisualPlanImportSuccess
  | VisualPlanImportFailure

export const isVisualScreen = (value: unknown): value is VisualScreen => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false
  }
  const screen = value as Record<string, unknown>
  return (
    typeof screen.id === 'string' &&
    typeof screen.image_index === 'number' &&
    Array.isArray(screen.shapes)
  )
}

export const countVisualPlanObjects = (screens: VisualScreen[]): number =>
  screens.reduce((total, screen) => total + screen.shapes.length, 0)

export const visualPlanContentCounts = (screens: VisualScreen[]) => ({
  screenCount: screens.length,
  objectCount: countVisualPlanObjects(screens),
})

export const encodeVisualPlanScreens = (screens: VisualScreen[]): string => {
  const payload: VisualPlanTransferPayload = { screens }
  return encodeJsonBase64(payload)
}

export const parseVisualPlanImport = (
  input: string,
): VisualPlanImportResult => {
  const trimmed = input.trim()
  if (trimmed === '') {
    return { ok: false, error: 'invalid' }
  }

  try {
    const parsed = decodeJsonBase64(trimmed)

    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return { ok: false, error: 'invalid' }
    }

    const screens = (parsed as Record<string, unknown>).screens
    if (!Array.isArray(screens) || !screens.every(isVisualScreen)) {
      return { ok: false, error: 'invalid' }
    }

    return {
      ok: true,
      screens,
      objectCount: countVisualPlanObjects(screens),
      screenCount: screens.length,
    }
  } catch {
    return { ok: false, error: 'invalid' }
  }
}
